import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { api } from "./api";

export type Role = "super_admin" | "admin_ops" | "inputer" | "viewer" | "kepala_koperasi" | "siswa";
export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  role_label: string;
  must_change_password?: boolean;
  student_id?: string | null;
}

export const ADMIN_ROLES: Role[] = ["super_admin", "admin_ops"];
export const WRITE_ROLES: Role[] = ["super_admin", "admin_ops", "inputer"];
export const ALL_ROLES: Role[] = ["super_admin", "admin_ops", "inputer", "viewer", "kepala_koperasi"];
export const FUNDING_ROLES: Role[] = ["super_admin", "kepala_koperasi"];

interface AuthCtx {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginGoogleSession: (sessionId: string) => Promise<void>;
  logout: () => void;
  can: (roles: Role[]) => boolean;
}

const Ctx = createContext<AuthCtx>(null as any);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("yef_token");
    if (!t || window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("yef_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("yef_token", data.token);
    setUser(data.user);
  }, []);

  const loginGoogleSession = useCallback(async (sessionId: string) => {
    const { data } = await api.post("/auth/google-session", { session_id: sessionId });
    localStorage.setItem("yef_token", data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("yef_token");
    setUser(null);
  }, []);

  const can = useCallback((roles: Role[]) => !!user && roles.includes(user.role), [user]);

  return <Ctx.Provider value={{ user, loading, login, loginGoogleSession, logout, can }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
